import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Two-way sync between a numeric step in component state and a URL `?step=` query param.
 * - On mount / URL change: if the URL has a valid step, push it into state via `setStep`.
 * - When `step` changes in state: replace the URL so refresh + back/forward preserve position.
 *
 * Steps are 1-indexed in the URL but the underlying state stays 0-indexed.
 * The URL is canonicalized: an out-of-range or malformed `?step=` is rewritten
 * to the clamped/current value rather than left to disagree with state.
 */
export function useStepParam(
  step: number,
  setStep: (n: number) => void,
  opts: { min?: number; max: number; paramName?: string; enabled?: boolean } = { max: 5 }
) {
  // `enabled: false` turns the hook off entirely — for callers that only want
  // the sync in some modes. (Live ApplicantFlow owns its URL and used to pass
  // sentinel args instead, which made the init stamp write a junk
  // `?__noop_step__=0` onto every live applicant URL.)
  const { min = 0, max, paramName = "step", enabled = true } = opts;
  const [searchParams, setSearchParams] = useSearchParams();
  const didInit = useRef(false);
  // The step value both sides currently agree on. Guards the two effects
  // against echoing each other: when state changed BECAUSE the URL said so,
  // the state->URL effect must not write the (stale) pre-change step back
  // into the URL. Without this, mounting with a URL step that differs from
  // the initial state (any mid-flow browser refresh: state resets to 0, URL
  // still says ?step=N) had the two effects perpetually chasing each other's
  // one-render-stale closures — an infinite replaceState/remount loop at
  // animation-frame rate (found by J4's reload assertion, 2026-07-09).
  const synced = useRef<number | null>(null);
  // Previous step this hook observed; lets the state->URL effect distinguish
  // a real state change from its own mount run (whose closure holds the
  // first-render step, NOT the current one, when the URL->state effect
  // already flushed a setStep in the same effects pass).
  const prevStep = useRef<number | null>(null);

  const clamp = (n: number) => Math.min(Math.max(n, min), max);
  // Single writer for the param — functional update so a concurrent
  // setSearchParams (e.g. VoucherFlow's invitation->session token swap) is
  // never clobbered by a stale snapshot. Always writes the clamped value.
  const writeStep = (n: number) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(paramName, String(clamp(n) + 1));
        return next;
      },
      { replace: true },
    );

  // URL -> state
  useEffect(() => {
    if (!enabled) return;
    const raw = searchParams.get(paramName);
    if (raw == null) {
      if (!didInit.current) {
        didInit.current = true;
        synced.current = clamp(step);
        writeStep(step);
      }
      return;
    }
    // Init even when the param is malformed — bailing before this used to
    // leave didInit false forever, permanently disabling the state->URL sync
    // for the whole session after one bad `?step=abc`.
    didInit.current = true;
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) {
      // Unrecoverable value: canonicalize to the current step.
      synced.current = clamp(step);
      writeStep(step);
      return;
    }
    const target = clamp(parsed - 1);
    synced.current = target;
    // Canonicalize an out-of-range / denormal value (e.g. ?step=9 with max 4)
    // so the URL never disagrees with where the user actually is.
    if (String(target + 1) !== raw) writeStep(target);
    if (target !== step) setStep(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, enabled]);

  // state -> URL
  useEffect(() => {
    if (!enabled) return;
    const prev = prevStep.current;
    prevStep.current = step;
    // Skip the mount run (prev === null): if the URL->state effect just
    // scheduled a different step, this closure's `step` is already stale.
    if (prev === null || prev === step) return;
    if (!didInit.current) return;
    if (step === synced.current) return; // change originated from the URL
    synced.current = step;
    writeStep(step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, enabled]);
}
